import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { LazyLoadEvent, MenuItem, MessageService } from 'primeng/api';
import { ConfigService, BroadcastMessageService, AppDataService } from 'app/services';
import { Utils } from 'app/utils';
import {
    BroadcastUserTypeEnum, BroadcastChannelTypeModel, BroadcastMessageTypeModel,
    MenuPermissionConfig, MenuPermissionActionEnum, FieldTypeEnum
} from 'app/models';
import * as moment from 'moment';
import {ColumnSelectorComponent} from 'app/column-selector/column-selector.component';
import { TableLazyLoadEvent } from 'primeng/table';

export enum FeedbackMenuEnum {
    Ride = 0,
    DisciplinaryNewCases = 1,
    DisciplinaryInProcess = 2,
    DisciplinaryArchived = 3,
    BroadcastMessageDrivers = 4,
    BroadcastMessageCustomers = 5,
    Dashboard = 6
}

@Component({
    selector: 'broadcast-message',
    templateUrl: './broadcast-message.component.html',
    styleUrls: ['./broadcast-message.component.scss',
        '../../../../../styles/style-layout.scss',
        '../../../../../styles/paginator.scss'
    ],
    providers: [MessageService]
})

export class BroadcastMessageComponent implements OnInit {
    @Input()
    public menuPermissionActive!: string;

    public onboardingTitle: string = 'Broadcast Messages';
    public menuTitle: string = '';

    public broadcastUserTypeActive: BroadcastUserTypeEnum = BroadcastUserTypeEnum.Drivers;

    public feedbackMenuEnum = FeedbackMenuEnum;
    public feedbackMenuActive!: FeedbackMenuEnum;
    @Input()
    set _feedbackMenuActive(value: FeedbackMenuEnum) {
        this.feedbackMenuActive = value;
        this.setOnboardingTitle(value);
        if (!this.lazyLoadHandled) {
            this.handleLazyLoad({
                first: 0,
                rows: this.rowsPerPage
            });
        }
    }
    private lazyLoadHandled = true;

    @ViewChild(ColumnSelectorComponent)
    public columnSelector!: ColumnSelectorComponent;

    public broadcastMessageId: number = 0;
    public showBroadcastMessageDetailSideBar: boolean = false;
    public showBroadcastMessageCreateSideBar: boolean = false;
    public breadcrumbs: MenuItem[] = [{ icon: 'icon_comms' }, { label: 'Comms' }, { label: 'Broadcast Message' }, { label: this.menuTitle }];

    constructor(
        private messageService: MessageService,
        private broadcastMessageService: BroadcastMessageService,
        public configService: ConfigService, public appDataService: AppDataService
    ) { }

    ngOnInit(): void { }

    // Grid settings
    public rowsPerPage: number = 50;
    public rowsPerPageOptions: Array<number> = [50, 100, 500, 1000];
    public totalRecords: number = -1;

    public first: number = 0;
    public sortField: string | string[] = '';
    private sortOrder: number = 1;

    public filter: {
        searchName: string;
        startDateJoin: Date | undefined;
        endDateJoin: Date | undefined;
    } = { searchName: '', startDateJoin: undefined, endDateJoin: undefined };

    public loading: boolean = true;
    private searchTimer: number = 0;

    public broadcastMessages: any = [];
    public broadcastMessageHeaderSearch: any = {
        messageId: null,
        title: null,
        dateAndTime: null,
        sender: null,
        receiverType: null,
        message: null,
        channel: null,
        broadcastMessageTypes: null,
        broadcastChannelTypes: null
    };
    public arrayFilterSenders: any = [{name: 'All', value: null}];
    public arrayFilterReceiverTypes: any = [{name: 'All Types', value: null}];
    public arrayFilterMessageTypes: any = [{name: 'All', value: null},{name: 'None', value: 0}].concat(BroadcastMessageTypeModel.BroadcastMessageTypeList);
    public arrayFilterChannelTypes: any = [{name: 'All', value: null}].concat(BroadcastChannelTypeModel.BroadcastChannelTypeList);

    public isVisibleBroadcastMessageFilter: boolean = false;

    public cols: any[] = [
        { field: 'Id', header: 'Message ID', width: '100px', fieldType: FieldTypeEnum.InputNumber,
            model: 'messageId', placeholder: 'Type here', active: false },
        { field: 'MessageTitle', header: 'Title', width: '250px', fieldType: FieldTypeEnum.Inputs,
            model: 'title', placeholder: 'Type here', active: false },
        { field: 'BroadcastStartDateTime', header: 'Date and Time', width: '100px', fieldType: FieldTypeEnum.Calendar,
            model: 'dateAndTime', placeholder: 'Select Date', active: false },
        { field: 'Sender', header: 'Sender', width: '100px', fieldType: FieldTypeEnum.Dropdown,
            model: 'sender', options: this.arrayFilterSenders, placeholder: 'Please Select', active: false },
        { field: 'ReceiverType', header: 'Receiver Type', width: '100px', fieldType: FieldTypeEnum.Dropdown,
            model: 'receiverType', options: this.arrayFilterReceiverTypes, placeholder: 'Please Select', active: false },
        { field: 'BroadcastMessageType', header: 'Message Type', width: '100px', fieldType: FieldTypeEnum.Dropdown,
            model: 'message', options: this.arrayFilterMessageTypes, placeholder: 'Please Select', active: false },
        { field: 'BroadcastChannelType', header: 'Channel', width: '100px', fieldType: FieldTypeEnum.Dropdown,
            model: 'channel', options: this.arrayFilterChannelTypes, placeholder: 'Please Select', active: false }
    ];
    public defaultCols = this.cols.map(x => x);

    public onSelectedColumnsChange(event: any[]) {
        if (event && event.length > 0) {
            this.cols = event;
        } else {
            this.cols = this.defaultCols;
        }
    }

    public setOnboardingTitle(value: FeedbackMenuEnum) {
        switch (value) {
            case FeedbackMenuEnum.BroadcastMessageDrivers:
                this.onboardingTitle = 'Broadcast Messages - Drivers';
                this.menuTitle = 'Drivers';
                this.broadcastUserTypeActive = BroadcastUserTypeEnum.Drivers;
                break;
            case FeedbackMenuEnum.BroadcastMessageCustomers:
                this.onboardingTitle = 'Broadcast Messages - Customers';
                this.menuTitle = 'Customers';
                this.broadcastUserTypeActive = BroadcastUserTypeEnum.Customers;
                break;
            default:
                this.onboardingTitle = 'Broadcast Messages';
                this.menuTitle = 'Operators';
                this.broadcastUserTypeActive = BroadcastUserTypeEnum.Drivers;
                break;
        }
    }

    public handleLazyLoad(event: TableLazyLoadEvent): void {
        const skip = event.first ? event.first : 0;

        if (event.rows) {
            this.rowsPerPage = event.rows;
        }

        this.sortField = event.sortField ? event.sortField : '';

        if (event.sortOrder) {
            this.sortOrder = event.sortOrder;
        }

        if (this.columnSelector) {
            this.columnSelector.loadTableSettings(this.onboardingTitle);
        }

        this.getBroadcastMessages(skip);

        this.lazyLoadHandled = false;
        this.breadcrumbs = [{ icon: 'icon_comms' }, { label: 'Comms' }, { label: 'Broadcast Message' }, { label: this.menuTitle }];
    }

    public getBroadcastMessages(skip: number) {
        this.loading = true;

        const dto = {
            BroadcastUserType: this.broadcastUserTypeActive,
            BroadcastMessageId: this.broadcastMessageHeaderSearch.messageId,
            MessageTitle: this.broadcastMessageHeaderSearch.title,
            BroadcastStartDateTime: (!this.broadcastMessageHeaderSearch.dateAndTime) ? null :
                moment(this.broadcastMessageHeaderSearch.dateAndTime).format('YYYY-MM-DDThh:mm:ss'),
            SenderUserId: this.broadcastMessageHeaderSearch.sender,
            BroadcastReceiverType: this.broadcastMessageHeaderSearch.receiverType,
            BroadcastMessageType: this.broadcastMessageHeaderSearch.message,
            BroadcastMessageTypes: this.broadcastMessageHeaderSearch.broadcastMessageTypes,
            BroadcastChannelType: this.broadcastMessageHeaderSearch.channel,
            BroadcastChannelTypes: this.broadcastMessageHeaderSearch.broadcastChannelTypes,
            ClientLocationType: this.appDataService.getClientLocationType(),
            Skip: skip,
            Take: this.rowsPerPage,
            SortField: this.sortField,
            SortOrder: this.sortOrder,
            TimeZoneOffset: Utils.getTimezoneOffset()
        };

        this.broadcastMessageService.getBroadcastMessages(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.loading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.first = skip;
                this.totalRecords = data.TotalRecords;
                this.broadcastMessages = data.Items;
                this.broadcastMessages.forEach((item: any) => {
                    item.StartDateTime = moment(item.BroadcastStartDateTime).format('D MMM YYYY, hh:mm A');
                    this.mergeSenderDuplicates(item.SenderUserName, item.SenderUserId);
                    this.mergeReceiverTypeDuplicates(item.BroadcastReceiverTypeText, item.BroadcastReceiverType);
                });
                this.loading = false;
            })
            .catch(err => {
                this.loading = false;
                throw err;
            });
    }

    public handleHeaderFilterChange(model: string, event: any) {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer);
        }

        this.searchTimer = window.setTimeout(() => {
            if (model === 'title') {
                this.broadcastMessageHeaderSearch.title = event.target.value;
            }
            else if (model === 'sender') {
                if (event === 0) {
                    this.broadcastMessageHeaderSearch.sender = null;
                }
            }
            else if (model === 'receiverType') {
                if (event === 0) {
                    this.broadcastMessageHeaderSearch.receiverType = null;
                }
            }
            else if (model === 'broadcastMessageType') {
                if (event === 0) {
                    this.broadcastMessageHeaderSearch.message = null;
                    this.broadcastMessageHeaderSearch.broadcastMessageTypes = null;
                }
            }
            else if (model === 'broadcastChannelType') {
                if (event === 0) {
                    this.broadcastMessageHeaderSearch.channel = null;
                    this.broadcastMessageHeaderSearch.broadcastChannelTypes = null;
                }
            }
            this.getBroadcastMessages(0);
            this.searchTimer = 0;
        }, 1500);
    }

    public mergeSenderDuplicates(name: string, value: any) {
        const exists = this.arrayFilterSenders.some((obj: any) => obj.name === name);
        if (!exists) {
            this.arrayFilterSenders.push({
                name,
                value
            });
        }
    }
    public mergeReceiverTypeDuplicates(name: string, value: any) {
        const exists = this.arrayFilterReceiverTypes.some((obj: any) => obj.value === value);
        if (!exists) {
            this.arrayFilterReceiverTypes.push({
                name,
                value
            });
        }
    }

    public handleFilterChange(): void {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer);
        }

        this.searchTimer = window.setTimeout(() => {
            this.getBroadcastMessages(0);
            this.searchTimer = 0;
        }, 1500);
    }

    public handleFilterClear(): void {
        this.filter.searchName = '';

        this.handleFilterChange();
    }

    public openBroadcastMessageDetailSideBar(broadcastMessageId: number) {
        this.broadcastMessageId = broadcastMessageId;
        this.showBroadcastMessageDetailSideBar = true;
    }
    changedShowBroadcastMessageDetailSideBarEmit(value: any) {
        this.showBroadcastMessageDetailSideBar = value;
    }

    public openBroadcastMessageCreateSideBar() {
        if (this.appDataService.hasPermission(this.menuPermissionActive, MenuPermissionActionEnum.Create) ||
            this.appDataService.hasPermission(this.menuPermissionActive, MenuPermissionActionEnum.Update)) {
            this.showBroadcastMessageCreateSideBar = true;
        }
        else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Access denied' });
        }
    }
    changedShowBroadcastMessageCreateSideBarEmit(value: any) {
        this.showBroadcastMessageCreateSideBar = value;
    }

    public openBroadcastMessageFilterSideBar() {
        this.isVisibleBroadcastMessageFilter = true;
    }
    changedVisibleBroadcastMessageFilterEmit(value: any) {
        this.isVisibleBroadcastMessageFilter = value;
    }

    protected readonly FieldTypeEnum = FieldTypeEnum;
}
